import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Wine, Pencil } from "lucide-react";
import { useCreateLot, WineLot } from "@/hooks/useWineLots";
import { useVarieties } from "@/hooks/useVarieties";
import { useVolumeUnit } from "@/hooks/useOrganization";
import { ClientSelect } from "@/components/shared/ClientSelect";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface CreateLotDialogProps {
  defaultClientId?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSuccess?: (lot: WineLot) => void;
  hideTrigger?: boolean;
}

export function CreateLotDialog({
  defaultClientId,
  open: controlledOpen,
  onOpenChange,
  onSuccess,
  hideTrigger = false,
}: CreateLotDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (onOpenChange || (() => {})) : setInternalOpen;

  const [clientId, setClientId] = useState(defaultClientId || "");
  const [varietyName, setVarietyName] = useState("");
  const [vintage, setVintage] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [volume, setVolume] = useState("");
  const [codeOverride, setCodeOverride] = useState("");
  const [isOverriding, setIsOverriding] = useState(false);

  const createLot = useCreateLot();
  const { data: varieties = [] } = useVarieties();
  const { volumeLabel } = useVolumeUnit();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // Fetch clients with codes
  const { data: clientsWithCodes = [] } = useQuery({
    queryKey: ["clients", "with-codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("client")
        .select("id, name, code")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []).filter((c: any) => c.is_active !== false);
    },
    enabled: isAuthenticated && !authLoading,
  });

  // Reverse lookup: clientId → code
  const clientCodeById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of clientsWithCodes) {
      if (c.code) map[c.id] = c.code.toUpperCase();
    }
    return map;
  }, [clientsWithCodes]);

  // Reverse lookup: variety name → code
  const varietyCodeByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const v of varieties) {
      map[v.name] = v.code.toUpperCase();
    }
    return map;
  }, [varieties]);

  // Auto-generate lot code from selections
  const generatedCode = useMemo(() => {
    const parts: string[] = [];

    // Client code (2 chars)
    const cc = clientId ? clientCodeById[clientId] : "";
    if (cc) parts.push(cc);

    // Vintage (2 digits)
    if (vintage && vintage.length === 4) {
      parts.push(vintage.slice(2));
    }

    // Variety code (2 chars)
    const vc = varietyName ? varietyCodeByName[varietyName] : "";
    if (vc) parts.push(vc);

    // Identifier suffix
    if (identifier) parts.push(identifier.toUpperCase());

    return parts.join("");
  }, [clientId, vintage, varietyName, identifier, clientCodeById, varietyCodeByName]);

  // The actual code that will be submitted
  const finalCode = isOverriding ? codeOverride : generatedCode;

  // Code segments for the preview display
  const codeSegments = useMemo(() => {
    if (isOverriding) return null;
    const segments: { text: string; label: string; color: string }[] = [];
    const cc = clientId ? clientCodeById[clientId] : "";
    if (cc) segments.push({ text: cc, label: "Client", color: "text-blue-600 dark:text-blue-400" });
    if (vintage && vintage.length === 4) segments.push({ text: vintage.slice(2), label: "Vintage", color: "text-amber-600 dark:text-amber-400" });
    const vc = varietyName ? varietyCodeByName[varietyName] : "";
    if (vc) segments.push({ text: vc, label: "Varietal", color: "text-emerald-600 dark:text-emerald-400" });
    if (identifier) segments.push({ text: identifier.toUpperCase(), label: "Identifier", color: "text-purple-600 dark:text-purple-400" });
    return segments;
  }, [isOverriding, clientId, vintage, varietyName, identifier, clientCodeById, varietyCodeByName]);

  const resetForm = () => {
    setClientId(defaultClientId || "");
    setVarietyName("");
    setVintage("");
    setIdentifier("");
    setVolume("");
    setCodeOverride("");
    setIsOverriding(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!finalCode) return;
    createLot.mutate(
      {
        code: finalCode,
        client_id: clientId,
        varietal: varietyName || undefined,
        vintage: vintage ? parseInt(vintage) : undefined,
        current_volume_l: volume ? parseFloat(volume) : undefined,
      },
      {
        onSuccess: (data) => {
          setOpen(false);
          resetForm();
          onSuccess?.(data as WineLot);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      setOpen(isOpen);
      if (!isOpen) resetForm();
    }}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <Wine className="mr-2 h-4 w-4" />
            Add Lot
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Wine Lot</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="client">Client *</Label>
            <ClientSelect
              value={clientId}
              onValueChange={setClientId}
              placeholder="Select client"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="vintage">Vintage</Label>
              <Input
                id="vintage"
                type="number"
                value={vintage}
                onChange={(e) => setVintage(e.target.value)}
                placeholder="ex 2024"
                min="1900"
                max="2100"
              />
            </div>
            <div>
              <Label htmlFor="volume">Initial Volume ({volumeLabel})</Label>
              <Input
                id="volume"
                type="number"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                placeholder="1000"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="varietal">Varietal</Label>
            <Select value={varietyName} onValueChange={setVarietyName}>
              <SelectTrigger>
                <SelectValue placeholder="Select varietal" />
              </SelectTrigger>
              <SelectContent>
                {varieties.map((v) => (
                  <SelectItem key={v.id} value={v.name}>
                    <div className="flex items-center gap-2">
                      {v.color && (
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: v.color }}
                        />
                      )}
                      {v.name} ({v.code})
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="identifier">Identifier</Label>
            <Input
              id="identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="e.g., BLEND, GV1A, SV6A"
              className="uppercase"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Vineyard, block, or fanciful name
            </p>
          </div>

          {/* Lot Code — auto-generated from selections */}
          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Lot Code</Label>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                onClick={() => {
                  if (isOverriding) {
                    setIsOverriding(false);
                    setCodeOverride("");
                  } else {
                    setCodeOverride(generatedCode);
                    setIsOverriding(true);
                  }
                }}
              >
                <Pencil className="h-3 w-3" />
                {isOverriding ? "Auto-generate" : "Edit manually"}
              </button>
            </div>

            {isOverriding ? (
              <Input
                value={codeOverride}
                onChange={(e) => setCodeOverride(e.target.value)}
                placeholder="Enter lot code"
                className="font-mono text-lg font-bold uppercase"
              />
            ) : (
              <div className="min-h-[40px] flex items-center">
                {generatedCode ? (
                  <div className="flex flex-col gap-1">
                    <span className="font-mono text-lg font-bold tracking-wide">
                      {codeSegments?.map((seg, i) => (
                        <span key={i} className={seg.color}>{seg.text}</span>
                      ))}
                    </span>
                    <div className="flex gap-2">
                      {codeSegments?.map((seg, i) => (
                        <span key={i} className={`text-[10px] ${seg.color} opacity-70`}>
                          {seg.label}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground italic">
                    Select client, vintage, and varietal to generate code
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createLot.isPending || !clientId || !finalCode}>
              {createLot.isPending ? "Creating..." : "Create Lot"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
